<template>
  <header class="flex flex-col gap-5 pb-6 pt-8 sm:gap-6 sm:pb-8 sm:pt-12">
    <!-- The banner on the venue's own wash, the same card the booking page
         heads with, so the menu and the booking read as one place. -->
    <div
      v-if="hasBanner"
      class="rounded-xl p-2 pb-5 sm:p-3 sm:pb-7"
      :style="tintStyle"
    >
      <PjVenueCover
        :accent="props.accent"
        :cover-srcset="props.coverSrcset"
        :cover-url="props.coverUrl"
        :logo-url="props.logoUrl"
        :name="props.name"
      >
        <template #caption>
          <h1 class="text-display-m text-ink sm:text-display-l">{{ props.name }}</h1>
          <PjVenueFacts v-bind="facts" />
        </template>
      </PjVenueCover>

      <!-- What the page offers in place of a menu, inside the same card, so a
           venue with nothing to browse still reads as one thing. -->
      <div
        v-if="$slots.default"
        class="mx-2 mt-7 border-t border-line px-1 pt-7 sm:mx-5 sm:mt-8 sm:px-3 sm:pt-8"
      >
        <slot />
      </div>
    </div>

    <div
      v-else
      class="flex items-center gap-4"
    >
      <div
        class="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-logo shadow-chip sm:h-16 sm:w-16"
        :style="{ backgroundColor: `var(--pj-venue-${props.accent})` }"
      >
        <img
          v-if="props.logoUrl && !logoFailed"
          alt=""
          class="h-full w-full object-cover"
          :src="props.logoUrl"
          @error="logoFailed = true"
        />
        <span
          v-else
          class="text-title font-bold text-white sm:text-display-s"
        >
          {{ venueMonogram(props.name) }}
        </span>
      </div>

      <div class="flex min-w-0 flex-col gap-1">
        <h1 class="text-display-m text-ink sm:text-display-l">{{ props.name }}</h1>
      </div>
    </div>

    <PjVenueFacts
      v-if="!hasBanner"
      v-bind="facts"
    />

    <PjTableStrip
      :has-open-tab="props.hasOpenTab"
      :participants="props.participants"
      :qr-enabled="props.qrEnabled"
      :tab-href="props.tabHref"
      :table-name="props.tableName"
    />
  </header>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'

import type { OrderingMode } from '@/domains/pjazza/types/consts/OrderingMode.consts.ts'
import type { PjazzaTabParticipant } from '@/domains/pjazza/types/models/PjazzaTab.types.ts'
import type { VenueAccent } from '@/domains/shared/types/consts/pjazza/VenueAccent.consts.ts'

import PjTableStrip from '@/domains/pjazza/components/molecules/pj-table-strip/PjTableStrip.vue'
import PjVenueCover from '@/domains/pjazza/components/molecules/pj-venue-cover/PjVenueCover.vue'
import PjVenueFacts from '@/domains/pjazza/components/molecules/pj-venue-facts/PjVenueFacts.vue'
import { venueMonogram } from '@/domains/pjazza/utils/venueMonogram.ts'
import { venueTintStyle } from '@/domains/pjazza/utils/venueTintStyle.ts'

const props = defineProps<{
  name: string
  accent: VenueAccent
  logoUrl?: string | null
  coverUrl?: string | null
  coverSrcset?: string | null
  /** Draws the banner on the accent even without a cover — for a venue with no menu below to push down. */
  isBanner?: boolean
  locality?: string | null
  addressLine?: string | null
  orderingMode: OrderingMode
  /** Set when a timed pause will lift by itself. */
  reopensAt?: string | null
  /** Whether collection is on offer, which the facts block states. */
  takeawayEnabled?: boolean
  /** Hides the table strip where the venue does not do table ordering. */
  qrEnabled?: boolean
  /** Set once this device has joined a table here, or chosen one. */
  tableName?: string | null
  /** False while the table is only a choice, with no rounds behind it. */
  hasOpenTab?: boolean
  /** Everyone who has sent a round to this table. */
  participants?: PjazzaTabParticipant[]
  tabHref: string
}>()

const logoFailed = ref(false)

// A venue with a cover shows it; one without keeps the compact header, so its menu starts higher.
const hasBanner = computed(() => !!props.coverUrl || props.isBanner)

const tintStyle = computed(() => venueTintStyle(props.accent))

// Beside the logo under a banner, below the name without one.
const facts = computed(() => ({
  addressLine: props.addressLine,
  locality: props.locality,
  orderingMode: props.orderingMode,
  qrEnabled: props.qrEnabled,
  reopensAt: props.reopensAt,
  takeawayEnabled: props.takeawayEnabled
}))
</script>
